/**
 * Dispatcher - Event queue management and delivery
 *
 * Manages the pending event queue, validates events before enqueueing them,
 * splits into chunks of at most 25 events and sends them to the backend with
 * retry logic. Handles periodic auto-flush and immediate flush for critical
 * events. Worker-safe.
 */

import { ADR_CONSTANTS } from '../core/constants.js';
import Logger from '../core/logger.js';
import { sanitizeUrl } from '../security/evidence-sanitizer.js';

const ALLOWED_EVENT_TYPES = [
	ADR_CONSTANTS.EVENT_TYPES.XSS,
	ADR_CONSTANTS.EVENT_TYPES.CSRF,
	ADR_CONSTANTS.EVENT_TYPES.SSRF,
	ADR_CONSTANTS.EVENT_TYPES.NOSQL_INJECTION,
	ADR_CONSTANTS.EVENT_TYPES.PROTOTYPE_POLLUTION,
	ADR_CONSTANTS.EVENT_TYPES.OPEN_REDIRECT,
	'injection_attempt',
	'injection',
	'sql_injection',
	'command_injection',
	'path_traversal'
];

export class Dispatcher {
	constructor(config, options = {}) {
		this.config = config;
		this.logger = new Logger(this.config);
		this.pendingEvents = [];
		this.flushTimer = null;
		this.isFlushing = false;
		this._memorySessionId = null;
		/** @type {import('../core/sensor-integrity.js').SensorIntegrity|null} */
		this.sensorIntegrity = options.sensorIntegrity || null;
		/** @type {import('../auth/session-token-manager.js').SessionTokenManager|null} */
		this.tokenManager = options.tokenManager || null;
	}

	start() {
		if (this.flushTimer) return;
		const interval = this.config.get('flushIntervalMs');
		this.flushTimer = setInterval(() => this.flush(), interval);
		this.logger.debug(`Auto-flush started every ${interval}ms`);
	}

	stop() {
		if (this.flushTimer) {
			clearInterval(this.flushTimer);
			this.flushTimer = null;
			this.logger.debug('Auto-flush stopped');
		}
	}

	enqueue(eventType, detectedPayload, severity = ADR_CONSTANTS.SEVERITY.MEDIUM, meta = null) {
		if (!this.isValidSeverity(severity)) {
			this.logger.warn(`Invalid severity: ${severity}, using 'medium'`);
			severity = ADR_CONSTANTS.SEVERITY.MEDIUM;
		}

		if (!this.meetsMinSeverity(severity)) {
			this.logger.debug(`Event ${eventType} discarded (severity ${severity} < ${this.config.get('minSeverity')})`);
			return false;
		}

		const event = this.createEvent(eventType, detectedPayload, severity, meta);
		
		if (!this.validateEvent(event)) {
			this.logger.error('Invalid event, not added to queue');
			return false;
		}

		// Prevenir memory leaks: si cola llena, descartar según severidad
		const maxQueueSize = this.config.get('maxQueueSize') || 1000;
		if (this.pendingEvents.length >= maxQueueSize) {
			if (severity === ADR_CONSTANTS.SEVERITY.CRITICAL) {
				// Críticos: descartar el más antiguo para hacer espacio
				this.pendingEvents.shift();
				this.logger.warn('Queue full, discarding oldest event to make room for critical event');
			} else {
				// No críticos: descartar el nuevo
				this.logger.warn(`Queue full (${maxQueueSize} events), discarding non-critical event`);
				return false;
			}
		}

		this.pendingEvents.push(event);
		this.logger.debug(`Event queued: ${eventType} (${severity}), queue: ${this.pendingEvents.length}`);

		if (severity === ADR_CONSTANTS.SEVERITY.CRITICAL) {
			this.flush();
		}

		return true;
	}

	createEvent(eventType, detectedPayload, severity, meta = null) {
		const mode = this.config.get('mode');
		const actionTaken = this.getActionTaken(mode);

		const defaultMeta = this.buildDefaultMeta();
		let eventMeta;
		
		// Si meta es array, usarlo tal cual; si es objeto, mergear con defaults
		if (meta && typeof meta === 'object') {
			if (Array.isArray(meta)) {
				eventMeta = meta;
			} else {
				eventMeta = { ...defaultMeta, ...meta };
			}
		} else {
			eventMeta = defaultMeta;
		}

		const payload = this.normalizePayload(detectedPayload);

		// Prioridad: origin_url de meta > window.location.href > ''
		const rawOriginUrl =
			(meta && typeof meta === 'object' && !Array.isArray(meta) && typeof meta.origin_url === 'string' && meta.origin_url) ||
			(typeof window !== 'undefined' && window.location ? window.location.href : '');

		// Centralised URL sanitisation. Strips values from sensitive query
		// params (token, session, api_key, auth, secret, etc.) so customer-
		// page URLs don't leak credentials to the IRIS endpoint on every
		// detector hit. Single point of enforcement covers every detector
		// that flows through enqueue(). Added 2026-05-25 per
		// security-review-iris-scanners-2026-05-25.md finding #6.
		const originUrl = sanitizeUrl(rawOriginUrl);

		// Also sanitize any meta.url that detectors may have set with the
		// pre-sanitisation value. Belt-and-braces: detector-side sanitisation
		// (e.g., injection-monitor.js) is preferred; this is the safety net.
		if (eventMeta && typeof eventMeta === 'object' && !Array.isArray(eventMeta)) {
			if (typeof eventMeta.url === 'string' && eventMeta.url) {
				eventMeta.url = sanitizeUrl(eventMeta.url);
			}
			if (typeof eventMeta.origin_url === 'string' && eventMeta.origin_url) {
				eventMeta.origin_url = sanitizeUrl(eventMeta.origin_url);
			}
		}

		const event = {
			event_type: eventType,
			severity: severity,
			origin_url: originUrl,
			detected_payload: payload,
			action_taken: actionTaken,
			meta: eventMeta,
			ts: Date.now()
		};

		if (eventType === ADR_CONSTANTS.EVENT_TYPES.XSS && typeof detectedPayload === 'string') {
			const maxLength = 2048;
			event.payload_sample = detectedPayload.length > maxLength 
				? detectedPayload.substring(0, maxLength) 
				: detectedPayload;
		}

		if (eventMeta && typeof eventMeta === 'object' && !Array.isArray(eventMeta)) {
			if (eventMeta.variant) {
				event.variant = eventMeta.variant;
				delete eventMeta.variant;
			}
			if (eventMeta.cvss_score !== undefined) {
				event.cvss_score = eventMeta.cvss_score;
				delete eventMeta.cvss_score;
			}
			if (eventMeta.cvss_vector) {
				event.cvss_vector = eventMeta.cvss_vector;
				delete eventMeta.cvss_vector;
			}
			if (eventMeta.cwe_id) {
				event.cwe_id = eventMeta.cwe_id;
				delete eventMeta.cwe_id;
			}
			if (eventMeta.mitre_technique) {
				event.mitre_technique = eventMeta.mitre_technique;
				delete eventMeta.mitre_technique;
			}
		}

		return event;
	}

	normalizePayload(payload) {
		if (typeof payload === 'string') {
			return payload;
		}
		// Backend validates detected_payload as a plain string (StoreAdrEventRequest);
		// an object here (e.g. CSRFMonitor's {method, url, headers_sample}) was being
		// sent unstringified and rejected with 422. AdrEventService::normalizeJsonField()
		// already round-trips a JSON string correctly, so stringify here.
		if (typeof payload === 'object' && payload !== null) {
			try {
				return JSON.stringify(payload);
			} catch (e) {
				return String(payload);
			}
		}
		return String(payload);
	}

	buildDefaultMeta() {
		const defaultMeta = {};

		if (typeof navigator !== 'undefined' && navigator.userAgent) {
			defaultMeta.user_agent = navigator.userAgent;
		}

		try {
			defaultMeta.session_id = this.getSessionId();
		} catch (e) {
			// Ignore
		}

		return defaultMeta;
	}

	getActionTaken(mode) {
		const map = {
			[ADR_CONSTANTS.MODES.OBSERVE]: ADR_CONSTANTS.ACTION_TAKEN.LOGGED,
			[ADR_CONSTANTS.MODES.BLOCK]: ADR_CONSTANTS.ACTION_TAKEN.BLOCKED,
			[ADR_CONSTANTS.MODES.REPORT]: ADR_CONSTANTS.ACTION_TAKEN.LOGGED
		};
		return map[mode] || ADR_CONSTANTS.ACTION_TAKEN.LOGGED;
	}

	validateEvent(event) {
		if (!event.event_type || typeof event.event_type !== 'string') return false;
		
		if (!ALLOWED_EVENT_TYPES.includes(event.event_type.toLowerCase())) {
			this.logger.warn(`Event type '${event.event_type}' is not supported by backend, discarding`);
			return false;
		}
		
		if (!event.severity || typeof event.severity !== 'string') return false;
		if (!event.origin_url || typeof event.origin_url !== 'string') return false;
		
		try {
			new URL(event.origin_url);
			if (event.origin_url.length > 2048) {
				this.logger.warn('origin_url exceeds 2048 characters, discarding event');
				return false;
			}
		} catch (e) {
			this.logger.warn('Invalid origin_url format, discarding event');
			return false;
		}
		
		if (event.detected_payload === undefined || event.detected_payload === null) return false;
		if (typeof event.ts !== 'number') return false;
		
		const now = Date.now();
		const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
		if (event.ts < thirtyDaysAgo || event.ts > now) {
			this.logger.warn('Timestamp (ts) is out of valid range, discarding event');
			return false;
		}
		
		if (!event.meta || typeof event.meta !== 'object') return false;
		return true;
	}

	isValidSeverity(severity) {
		return Object.values(ADR_CONSTANTS.SEVERITY).includes(severity);
	}

	meetsMinSeverity(severity) {
		const levels = [
			ADR_CONSTANTS.SEVERITY.LOW,
			ADR_CONSTANTS.SEVERITY.MEDIUM,
			ADR_CONSTANTS.SEVERITY.HIGH,
			ADR_CONSTANTS.SEVERITY.CRITICAL
		];
		const minSeverity = this.config.get('minSeverity');
		const eventLevel = levels.indexOf(severity);
		const minLevel = levels.indexOf(minSeverity);
		return eventLevel >= minLevel;
	}

	async flush() {
		if (this.isFlushing || this.pendingEvents.length === 0) return;

		this.isFlushing = true;
		const events = [...this.pendingEvents];
		this.pendingEvents = [];

		this.logger.debug(`Flushing ${events.length} event(s)`);

		try {
			const MAX_EVENTS_PER_BATCH = 25;
			const chunks = [];
			for (let i = 0; i < events.length; i += MAX_EVENTS_PER_BATCH) {
				chunks.push(events.slice(i, i + MAX_EVENTS_PER_BATCH));
			}

			let totalAccepted = 0;
			let totalRejected = 0;

			// Enviar cada chunk secuencialmente
			for (let i = 0; i < chunks.length; i++) {
				const chunk = chunks[i];
				this.logger.debug(`Sending chunk ${i + 1}/${chunks.length} (${chunk.length} events)`);
				
				try {
					const result = await this.sendBatch(chunk);
					if (result && result.accepted !== undefined) {
						totalAccepted += result.accepted || 0;
						totalRejected += result.rejected || 0;
					}
				} catch (error) {
					// Timeout: asumir éxito (backend procesó pero respuesta lenta)
					if (error._assumedSuccess) {
						this.logger.info(`Chunk ${i + 1} sent (timeout but assumed processed by backend)`);
						continue;
					}
					
					this.logger.error(`Error sending chunk ${i + 1}:`, error.message);
					// Errores permanentes: no re-enviar
					if (error.message.includes('Authentication failed') || error.message.includes('Validation failed')) {
						continue;
					}
					if (error._exhaustedRetries) {
						this.logger.warn(`Chunk ${i + 1} discarded (CORS or permanent error)`);
						continue;
					}
					// Errores temporales: re-encolar para siguiente flush
					this.pendingEvents.push(...chunk);
				}
			}

			if (totalRejected > 0) {
				this.logger.warn(`${totalRejected} event(s) were rejected by backend`);
			}
			if (totalAccepted > 0) {
				this.logger.debug(`Successfully sent ${totalAccepted} event(s)`);
			}
		} catch (error) {
			this.logger.error('Error sending events:', error.message);
			
			if (error._exhaustedRetries) {
				if (error.message.includes('CORS') || error.message.includes('Access-Control-Allow-Origin')) {
					this.logger.error('CORS error: Events discarded. Fix backend CORS configuration before retrying.');
				} else {
					this.logger.warn(`Events discarded after ${this.config.get('maxRetries')} failed attempts`);
				}
				return;
			}
			
			if (error.message.includes('Authentication failed') || error.message.includes('Validation failed')) {
				return;
			}
			
			this.pendingEvents.unshift(...events);
		} finally {
			this.isFlushing = false;
		}
	}

	async sendBatch(events) {
		const maxRetries = this.config.get('maxRetries');
		const retryDelays = this.config.get('retryDelays');
		
		for (let attempt = 0; attempt < maxRetries; attempt++) {
			try {
				const response = await this.makeRequest(events);
				
				if (response.ok) {
					const result = await response.json();
					if (result.accepted !== undefined) {
						this.logger.info(`Events processed: ${result.accepted} accepted, ${result.rejected || 0} rejected`);
					}
					return result;
				}

				// Auth errors: refresh once and retry once
				if (response.status === 401 || response.status === 403) {
					if (this.tokenManager) {
						try {
							await this.tokenManager.forceRefresh();
							const retryRes = await this.makeRequest(events);
							if (retryRes.ok) {
								const result = await retryRes.json();
								if (result.accepted !== undefined) {
									this.logger.info(`Events processed: ${result.accepted} accepted, ${result.rejected || 0} rejected`);
								}
								return result;
							}
							const errorData = await retryRes.json().catch(() => ({ message: retryRes.statusText }));
							throw new Error(`Authentication failed: ${errorData.message || retryRes.statusText}`);
						} catch (e) {
							const errorData = await response.json().catch(() => ({ message: response.statusText }));
							throw new Error(`Authentication failed: ${errorData.message || response.statusText}`);
						}
					}
					const errorData = await response.json().catch(() => ({ message: response.statusText }));
					throw new Error(`Authentication failed: ${errorData.message || response.statusText}`);
				}

				if (response.status === 422) {
					const errorData = await response.json().catch(() => ({ message: response.statusText }));
					this.logger.error('Validation error from backend:', errorData.errors || errorData.message);
					throw new Error(`Validation failed: ${errorData.message || 'Invalid event data'}`);
				}

				// Errores temporales: reintentar con backoff (429 rate limit, 5xx server errors)
				if (response.status === 429 || response.status >= 500) {
					if (attempt < maxRetries - 1) {
						const delay = retryDelays[attempt] || retryDelays[retryDelays.length - 1];
						this.logger.warn(`Retry ${attempt + 1}/${maxRetries} in ${delay}ms (status: ${response.status})`);
						await this.sleep(delay);
						continue;
					}
				}

				throw new Error(`HTTP ${response.status}: ${response.statusText}`);

			} catch (error) {
				// Errores de cliente: no reintentar (401/403/422)
				if (error.message.includes('Authentication failed') || error.message.includes('Validation failed')) {
					throw error;
				}

				if (error.message.includes('Request timeout')) {
					this.logger.warn('Request timeout - backend processed event but response is slow (likely processing in background). Assuming success to avoid duplicates.');
					error._exhaustedRetries = true;
					error._assumedSuccess = true;
					throw error;
				}

				if (error.message.includes('CORS') || error.message.includes('Access-Control-Allow-Origin') || 
				    (error.message === 'Failed to fetch' && typeof window !== 'undefined')) {
					error._exhaustedRetries = true;
					this.logger.error('CORS error detected - requests will not be retried. Check backend CORS configuration.');
					throw error;
				}

				if (attempt === maxRetries - 1) {
					error._exhaustedRetries = true;
					throw error;
				}

				const delay = retryDelays[attempt] || retryDelays[retryDelays.length - 1];
				this.logger.warn(`Retry ${attempt + 1}/${maxRetries} in ${delay}ms (error: ${error.message})`);
				await this.sleep(delay);
			}
		}
	}

	async makeRequest(events) {
		const endpoint = this.config.get('endpoint');
		const timeout = this.config.get('timeout');

		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		try {
			// Filtrar propiedades internas antes de enviar
			const cleanEvents = events.map(event => {
				const { _retryCount, ...cleanEvent } = event;
				return cleanEvent;
			});

			const headers = {
				'Content-Type': 'application/json',
				'Accept': 'application/json'
			};

			if (this.tokenManager) {
				const jwt = await this.tokenManager.getToken();
				headers['Authorization'] = `Bearer ${jwt}`;
			}

			// Sensor integrity hash on every request (backend validation)
			const sensorHash = this.sensorIntegrity
				? await this.sensorIntegrity.getSensorHash()
				: (this.config.get && this.config.get('sensorHash')) || null;
			if (sensorHash) {
				headers['X-Sensor-Integrity'] = sensorHash;
			}

			const response = await fetch(endpoint, {
				method: 'POST',
				headers,
				body: JSON.stringify({ events: cleanEvents }),
				signal: controller.signal
			});

			clearTimeout(timeoutId);
			return response;
		} catch (error) {
			clearTimeout(timeoutId);
			if (error.name === 'AbortError') {
				throw new Error('Request timeout');
			}
			throw error;
		}
	}

	sleep(ms) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	getSessionId() {
		// Intentar usar sessionStorage (main thread), fallback a memoria (worker)
		try {
			if (typeof sessionStorage !== 'undefined') {
				let sessionId = sessionStorage.getItem('adr_session_id');
				if (!sessionId) {
					sessionId = this.generateId();
					sessionStorage.setItem('adr_session_id', sessionId);
				}
				return sessionId;
			}
		} catch (e) {
		}

		// Fallback a memoria si sessionStorage no disponible
		if (!this._memorySessionId) {
			this._memorySessionId = this.generateId();
		}
		return this._memorySessionId;
	}

	generateId() {
		return Date.now().toString(36) + Math.random().toString(36).substring(2);
	}

	getQueueStatus() {
		return {
			pending: this.pendingEvents.length,
			flushing: this.isFlushing
		};
	}
}

export default Dispatcher;

